import React, { useState } from 'react';
import { Box, IconButton, useTheme, TextareaAutosize, Snackbar, Alert } from '@mui/material';
import { AttachFileOutlined, SendOutlined } from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';

const MarkdownInputWithImageUpload = ({
    isUserQueryPending,
    handleKeyDown,
    handleSubmit,
    uploadedFiles,
    setUploadedFiles,
    textRef
}) => {
    const theme = useTheme();

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("error");

    const handleOpenSnackbar = (message, severity = "error") => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
        setSnackbarMessage("");
    };

    const handleFileUpload = (files) => {
        const allowedTypes = ['image/jpeg', 'image/png'];
        const maxTotalSize = 5 * 1024 * 1024; // Example: total size limit (5 MB)
        const maxFiles = 3;

        // Calculate the total size of the current uploaded files
        const totalUploadedSize = uploadedFiles.reduce((total, file) => total + file.size, 0);

        // Calculate the size of the new files to be uploaded
        const totalNewFilesSize = Array.from(files).reduce((total, file) => total + file.size, 0);

        // Check if total size exceeds the limit
        if (totalUploadedSize + totalNewFilesSize > maxTotalSize) {
            handleOpenSnackbar("Total file size exceeds the limit of 5 MB.", "warning");
            return;
        }

        // Check if the number of files exceeds the limit
        if (uploadedFiles.length + files.length > maxFiles) {
            handleOpenSnackbar("You can only upload up to 3 images at a time.", "warning");
            return;
        }

        // Filter valid files based on file type and size
        const validFiles = Array.from(files).filter(file => {
            if (!allowedTypes.includes(file.type)) {
                handleOpenSnackbar("Only JPG and PNG images are allowed.", "error");
                return false;
            }

            if (file.size > maxTotalSize) {
                handleOpenSnackbar("File size should not exceed the total size limit.", "warning");
                return false;
            }

            return true;
        });

        // Add valid files to uploadedFiles state
        setUploadedFiles(prevFiles => [...prevFiles, ...validFiles]);
    };

    const handleFileChange = (event) => {
        handleFileUpload(event.target.files);

        // Reset the input value to allow re-upload of the same file
        event.target.value = null;
    };

    const handleDrop = (event) => {
        event.preventDefault();
        handleFileUpload(event.dataTransfer.files);
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const handleImageRemove = (index) => {
        setUploadedFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    };

    return (
        <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            sx={{
                position: 'sticky',
                bottom: 0,
                boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                borderRadius: 2,
                backgroundColor: 'background.paper',
                border: 2,
                borderColor: "primary.dark",
                gap: 1,
                padding: 1,
                "&:hover": {
                    border: 2,
                    borderColor: "info.dark",
                    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)',
                }
            }}
        >
            <IconButton
                component="label"
                disabled={isUserQueryPending}
                sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                        backgroundColor: 'primary.dark',
                        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)',
                    },
                    alignSelf: 'flex-end',
                }}
            >
                <AttachFileOutlined sx={{ fontSize: 28 }} />
                <input
                    type="file"
                    hidden
                    multiple
                    onChange={handleFileChange}
                />
            </IconButton>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    width: '100%',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        alignSelf: 'flex-start',
                    }}
                >
                    {uploadedFiles.map((file, index) => (
                        <Box key={index} sx={{ position: 'relative' }}>
                            <Box
                                sx={{
                                    // border: 1,
                                    // borderRadius: 1,
                                    // borderColor: 'grey.400',
                                }}
                            >
                                <img
                                    src={URL.createObjectURL(file)}
                                    alt="uploaded"
                                    style={{
                                        width: 50,
                                        height: 50,
                                        objectFit: 'cover',
                                        borderRadius: '8px',
                                    }}
                                />
                            </Box>
                            <IconButton
                                onClick={() => handleImageRemove(index)}
                                sx={{
                                    position: 'absolute',
                                    top: 2,
                                    right: 2,
                                    backgroundColor: theme.palette.grey[500],
                                    color: theme.palette.primary.contrastText,
                                    height: 20,
                                    width: 20,
                                    borderRadius: '50%',
                                    "&:hover": {
                                        backgroundColor: theme.palette.grey[600],
                                        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)',
                                    },
                                }}
                            >
                                ×
                            </IconButton>
                        </Box>
                    ))}
                </Box>

                <TextareaAutosize
                    minRows={1}
                    maxRows={4}
                    ref={textRef}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask me anything..."
                    style={{
                        width: '100%',
                        border: 'none',
                        outline: 'none',
                        resize: 'none',
                        fontFamily: 'Arial, sans-serif',
                        fontSize: '1.1rem',
                        fontWeight: "400",
                        color: theme.palette.text.primary,
                        lineHeight: '1.5',
                        padding: '12px 16px',
                        backgroundColor: 'transparent',
                        overflowY: 'auto',
                        scrollbarWidth: 'thin',
                        scrollbarColor: `${theme.palette.grey[400]} transparent`,
                    }}
                    sx={{
                        color: 'text.primary',
                    }}
                />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', alignSelf: 'flex-end', }}>
                {isUserQueryPending ? (
                    <CircularProgress size={40} />
                ) : (
                    <IconButton
                        onClick={() => {
                            const currentText = textRef.current.value.trim();
                            handleSubmit(currentText);
                        }}
                        sx={{
                            width: 40,
                            height: 40,
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                            '&:hover': {
                                backgroundColor: 'primary.dark',
                                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)',
                            },
                        }}
                    >
                        <SendOutlined sx={{ fontSize: 28 }} />
                    </IconButton>
                )}
            </Box>
            {/* Snackbar for Notifications */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: "100%" }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default MarkdownInputWithImageUpload;
